import { useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { tasksTableConfig } from './tasksTable.config';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';

export type AvailableColumnsType = {
  readingCode: boolean;
  patientCode: boolean;
  visitName: boolean;
  createdDate: boolean;
  scoringFormName: boolean;
  assigneeName: boolean;
  status: boolean;
  startReadingTime: boolean;
  finishReadingTime: boolean;
  action: boolean;
};

const COLUMNS_META: Array<{ key: keyof AvailableColumnsType; label: string }> = [
  { key: 'readingCode', label: 'Reading ID' },
  { key: 'patientCode', label: 'Subject ID' },
  { key: 'visitName', label: 'Visit Name' },
  { key: 'createdDate', label: 'Creation Date' },
  { key: 'scoringFormName', label: 'Reading Criteria' },
  { key: 'assigneeName', label: 'Reader' },
  { key: 'status', label: 'Status' },
  { key: 'startReadingTime', label: 'Started Date' },
  { key: 'finishReadingTime', label: 'Completion Date' },
  { key: 'action', label: 'Action' },
];

const initialColumns: AvailableColumnsType = {
  readingCode: true,
  patientCode: true,
  visitName: true,
  createdDate: true,
  scoringFormName: true,
  assigneeName: true,
  status: true,
  startReadingTime: true,
  finishReadingTime: true,
  action: true,
};

export const useReadingCompletedFilters = () => {
  const [availableColumns, setAvailableColumns] = useState<AvailableColumnsType>(initialColumns);
  const [searchValue, setSearchValue, debouncedSearchValue] = useDebouncedValue('');
  const readingCompletedTableColumns = tasksTableConfig();

  const displayFilters = useMemo(() => {
    return [
      {
        key: '1',
        type: 'group' as const,
        label: <div className='text-1216 text-spanishGray'>All Columns</div>,
        children: COLUMNS_META.map(({ key, label }) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={availableColumns[key]} />
            </div>
          ),
        })),
      },
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div
            role='button'
            className='text-1216 text-center text-spanishGray text-[14px] hover:cursor-pointer'
            onClick={() => {
              setAvailableColumns(initialColumns);
            }}
          >
            Reset to default
          </div>
        ),
      },
    ];
  }, [availableColumns]);

  const filteredTableColumns = useMemo(() => {
    const columns = readingCompletedTableColumns.filter(column => {
      const key = column.name as keyof AvailableColumnsType;
      return availableColumns[key];
    });

    return columns;
  }, [availableColumns, readingCompletedTableColumns]);

  const selectColumns = useCallback(
    (e: keyof AvailableColumnsType) => {
      setAvailableColumns(prevState => {
        return {
          ...prevState,
          [e]: !prevState[e],
        };
      });
    },
    [setAvailableColumns]
  );

  return {
    displayFilters,
    filteredTableColumns,
    availableColumns,
    selectColumns,
    searchValue,
    setSearchValue,
    debouncedSearchValue,
  };
};
